import styled from '@emotion/styled'
import { ExtranetActivatableProductPreviewDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetListItem,
  ExtranetListItemProps,
  htmlStringParser,
  MediaQuery,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacText, PharmacTruncate } from '@pharmac/ui'
import { useNavigate } from 'react-router-dom'
import ImageNotAvailable from '../../dose-dispensing-catalog/image-not-available/NoImageAvailable_16_9.jpg'
import { useShop } from '../useShop'

type ProductProps = {
  product: ExtranetActivatableProductPreviewDto
}

const ContentText = styled(PharmacText)`
  max-height: 100%;
  overflow: hidden;
`

const TopBarText = styled(PharmacText)`
  ${MediaQuery.maxMobile} {
    padding: var(--spacing-tiny) 0;
  }
`

export const ShopActivateProductPreviewItem = ({ product }: ProductProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const { minDesktop, minTablet } = useBreakpoint()
  const { formatTimecodeToFancyForm, langLabel } = useShop(product.id)

  const productCategory = (productFamily: string) => {
    if (productFamily === 'recording-product') return t('extranet:shopProducts.video')
    if (productFamily === 'document-product') return t('extranet:shopProducts.document')
  }

  const itemProps: ExtranetListItemProps = {
    border: true,
    image: product.image
      ? {
          desktop: product.image.desktopUrl,
          desktopLarge: product.image.desktopLargeUrl,
          mobile: product.image.mobileUrl,
        }
      : {
          desktop: ImageNotAvailable,
          desktopLarge: ImageNotAvailable,
          mobile: ImageNotAvailable,
        },
    title: PharmacTruncate(product.name, 75),
    labels: [
      <>
        {product.language?.code && (
          <TopBarText paddingSmallRight>{langLabel(String(product.language?.code))}</TopBarText>
        )}
        <TopBarText>{productCategory(product?.productFamily)}</TopBarText>
        {product.productFamily === 'recording-product' && (
          <ExtranetContainer
            alignCenter
            paddingSmallLeft
          >
            <PharmacIcon
              size="15px"
              icon="clock"
            />
            <PharmacText marginSmallLeft>
              {product.videoDuration && formatTimecodeToFancyForm(product.videoDuration)}
            </PharmacText>
          </ExtranetContainer>
        )}
      </>,
    ],
    content: (
      <ContentText
        dangerouslySetInnerHTML={{
          __html: htmlStringParser(
            PharmacTruncate(product.description, minDesktop ? 200 : minTablet ? 200 : 100) ?? ''
          ),
        }}
      />
    ),
    navigateFn: () => navigate(`${product.id}`),
    ariaDescription: 'Link to product',
  }

  return (
    <ExtranetListItem
      key={'product-' + product.id}
      {...itemProps}
    />
  )
}
